import { useEffect, useState } from 'react';
import { X, User, Mail, Calendar, Tag, Hash, Send, Loader2 } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useUpdateTicketStatusMutation,
  useAssignTicketMutation,
  useGetAllUsersQuery,
  useGetTicketByIdQuery,
  useReplyAdminTicketMutation,
} from '@/store/api/apiSlice';
import { SupportTicket, SupportMessage, TicketStatus, UserRole } from '@/types';

interface SupportTicketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: SupportTicket | null;
}

function formatWhen(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function readErrorMessage(err: any, fallback = 'Something went wrong'): string {
  return err?.data?.message ?? err?.message ?? fallback;
}

export default function SupportTicketDetailsModal({
  isOpen,
  onClose,
  ticket: ticketSummary,
}: SupportTicketDetailsModalProps) {
  const ticketId = ticketSummary?.id;

  // Always fetch the full detail (which carries messages[]) when open.
  // `refetchOnMountOrArgChange` makes sure that re-opening the modal pulls a
  // fresh thread instead of serving a stale cached copy — important because the
  // list endpoint omits messages[] but the cached row from there can otherwise
  // shadow a freshly-fetched detail.
  const { data: ticketDetail, isFetching, refetch: refetchTicket } =
    useGetTicketByIdQuery(ticketId || '', {
      skip: !ticketId || !isOpen,
      refetchOnMountOrArgChange: true,
    });
  const ticket = ticketDetail ?? ticketSummary;

  const [status, setStatus] = useState<TicketStatus>(ticket?.status ?? TicketStatus.OPEN);
  const [assignToAdmin, setAssignToAdmin] = useState<string>(ticket?.assignedTo?.id ?? '');
  const [replyBody, setReplyBody] = useState('');
  const [replyError, setReplyError] = useState('');
  const [statusError, setStatusError] = useState('');
  // Optimistic local bubbles for replies the admin sends from this session.
  // They sit alongside server messages until the next refetch lands a copy of
  // the same body, at which point the optimistic version is dropped to avoid
  // duplicates. Survives spotty backends that don't echo the new message back
  // in subsequent GETs.
  const [optimisticReplies, setOptimisticReplies] = useState<SupportMessage[]>([]);

  // When the modal is opened for a different ticket, wipe optimistic state so
  // we don't bleed messages across tickets.
  useEffect(() => {
    setOptimisticReplies([]);
  }, [ticketId]);

  // Re-hydrate when a different ticket is opened or the detail refreshes.
  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setAssignToAdmin(ticket.assignedTo?.id ?? '');
    }
  }, [ticket?.id, ticket?.status, ticket?.assignedTo?.id]);

  const [updateTicketStatus, { isLoading: isUpdating }] = useUpdateTicketStatusMutation();
  const [assignTicket, { isLoading: isAssigning }] = useAssignTicketMutation();
  const [replyAdmin, { isLoading: isReplying }] = useReplyAdminTicketMutation();
  const { data: users = [] } = useGetAllUsersQuery({ role: UserRole.SUPER_ADMIN });

  const handleSendReply = async () => {
    if (!ticket) return;
    const trimmed = replyBody.trim();
    if (!trimmed) {
      setReplyError('Type a reply before sending.');
      return;
    }
    setReplyError('');
    try {
      await replyAdmin({ id: ticket.id, data: { body: trimmed } }).unwrap();
      // Drop in an optimistic bubble so the admin sees their reply land
      // immediately, regardless of whether the backend echoes it back on the
      // next GET. The merge logic below dedupes once the server data refreshes.
      setOptimisticReplies((prev) => [
        ...prev,
        {
          id: `optimistic-${Date.now()}`,
          senderType: 'admin',
          senderName: 'You',
          body: trimmed,
          createdAt: new Date().toISOString(),
        },
      ]);
      setReplyBody('');
      // Belt-and-braces: even though the mutation invalidates the
      // SupportTicket tag, force a refetch in case the cache logic is gummed
      // up for any reason (or the response shape changed).
      refetchTicket();
    } catch (err: any) {
      setReplyError(readErrorMessage(err, 'Failed to send reply. Please try again.'));
    }
  };

  const handleUpdateStatus = async () => {
    if (!ticket) return;
    setStatusError('');
    try {
      await updateTicketStatus({ id: ticket.id, data: { status } }).unwrap();
      onClose();
    } catch (err: any) {
      setStatusError(readErrorMessage(err, 'Failed to update status.'));
    }
  };

  const handleAssignTicket = async () => {
    if (!ticket || !assignToAdmin) return;
    try {
      await assignTicket({ id: ticket.id, data: { adminId: assignToAdmin } }).unwrap();
      if (ticket.status === TicketStatus.OPEN) {
        await updateTicketStatus({ id: ticket.id, data: { status: TicketStatus.IN_PROGRESS } }).unwrap();
      }
    } catch (error) {
      console.error('Failed to assign ticket:', error);
    }
  };

  const getStatusColor = (s: TicketStatus | string) => {
    switch (s) {
      case TicketStatus.OPEN:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case TicketStatus.IN_PROGRESS:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case TicketStatus.WAITING_ON_EMPLOYEE:
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case TicketStatus.RESOLVED:
        return 'bg-green-50 text-green-700 border-green-100';
      case TicketStatus.CLOSED:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'bg-gray-50 text-gray-700 border-gray-100';
      case 'medium':
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'high':
        return 'bg-orange-50 text-orange-700 border-orange-100';
      case 'urgent':
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  if (!ticket) return null;

  // Build the thread: prefer messages[] from the API. Fall back to seeding from the
  // original message + legacy adminResponse so older tickets still render coherently.
  const apiMessages: SupportMessage[] = ticket.messages ?? [];
  const fallbackMessages: SupportMessage[] = (() => {
    if (apiMessages.length > 0) return [];
    const seed: SupportMessage[] = [];
    if (ticket.message) {
      seed.push({
        id: `seed-orig-${ticket.id}`,
        senderType: 'employee',
        senderName: ticket.employeeName || `${ticket.user?.firstName ?? ''} ${ticket.user?.lastName ?? ''}`.trim() || 'Requester',
        body: ticket.message,
        createdAt: ticket.createdAt,
      });
    }
    if (ticket.adminResponse) {
      seed.push({
        id: `seed-admin-${ticket.id}`,
        senderType: 'admin',
        senderName: ticket.assignedTo
          ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
          : 'Support',
        body: ticket.adminResponse,
        createdAt: ticket.updatedAt,
      });
    }
    return seed;
  })();
  const baseMessages = apiMessages.length > 0 ? apiMessages : fallbackMessages;
  // Show optimistic replies that haven't been mirrored back by the server yet.
  // Match on (senderType=admin + identical body) — exact text duplicates are
  // unlikely enough in a real conversation that this is a safe dedupe key.
  const serverAdminBodies = new Set(
    baseMessages.filter((m) => m.senderType === 'admin').map((m) => m.body.trim()),
  );
  const stillPending = optimisticReplies.filter((m) => !serverAdminBodies.has(m.body.trim()));
  // Sort defensively by createdAt ascending — the backend is supposed to
  // return them this way, but a flipped order on any read path would turn the
  // chat into a confusing puzzle.
  const messages = [...baseMessages, ...stillPending].sort((a, b) => {
    const ta = Date.parse(a.createdAt) || 0;
    const tb = Date.parse(b.createdAt) || 0;
    return ta - tb;
  });

  const isEmployeeTicket = !!ticket.supportCode || !!ticket.investigationId || !ticket.user;
  const requesterName =
    ticket.user?.firstName || ticket.user?.lastName
      ? `${ticket.user?.firstName ?? ''} ${ticket.user?.lastName ?? ''}`.trim()
      : ticket.employeeName || '—';
  const requesterEmail = ticket.user?.email || ticket.employeeEmail || '—';
  const requesterRole = isEmployeeTicket ? 'employee (consent form)' : ticket.user?.role || '—';

  const isLocked = status === TicketStatus.RESOLVED || status === TicketStatus.CLOSED;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Support Ticket Details</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {/* Ticket Info */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{ticket.subject}</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={`${getStatusColor(ticket.status)} px-3 py-1 rounded-lg font-medium capitalize`}>
                      {ticket.status.replace(/_/g, ' ')}
                    </Badge>
                    <Badge variant="outline" className={`${getPriorityColor(ticket.priority)} px-3 py-1 rounded-lg font-medium capitalize`}>
                      {ticket.priority}
                    </Badge>
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-600 border-indigo-100 px-3 py-1 rounded-lg font-medium capitalize">
                      {ticket.category}
                    </Badge>
                    {isEmployeeTicket && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 px-3 py-1 rounded-lg font-medium">
                        Employee
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Opened {formatWhen(ticket.createdAt)}</span>
                </div>
                {ticket.lastMessageAt && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>Last reply {formatWhen(ticket.lastMessageAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Requester info */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                {isEmployeeTicket ? 'Employee' : 'User'} Information
              </h4>
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium text-gray-900">{requesterName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">{requesterEmail}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Tag className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Role:</span>
                  <span className="font-medium text-gray-900 capitalize">{requesterRole}</span>
                </div>
                {ticket.supportCode && (
                  <div className="flex items-center gap-2 text-sm">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Support code:</span>
                    <span className="font-mono font-medium text-gray-900">{ticket.supportCode}</span>
                  </div>
                )}
                {ticket.investigationId && (
                  <div className="flex items-center gap-2 text-sm">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Investigation:</span>
                    <span className="font-mono font-medium text-gray-900 truncate">{ticket.investigationId}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Thread */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Conversation</h4>
              {isFetching && messages.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <p className="text-sm text-gray-500">No messages on this ticket yet.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {messages.map((m) => (
                    <AdminMessageBubble key={m.id} message={m} />
                  ))}
                </div>
              )}
            </div>

            {/* Reply */}
            {isLocked ? (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-600">
                This ticket is {status.replace(/_/g, ' ')}. Re-open it (change status) before replying.
              </div>
            ) : (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Reply</h4>
                <textarea
                  value={replyBody}
                  onChange={(e) => {
                    setReplyBody(e.target.value);
                    setReplyError('');
                  }}
                  placeholder="Write a reply — sent to the requester by email and added to this thread."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-y"
                />
                {replyError && <p className="mt-2 text-xs text-red-600">{replyError}</p>}
                <div className="flex justify-end mt-3">
                  <Button
                    onClick={handleSendReply}
                    disabled={isReplying}
                    className="bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                  >
                    {isReplying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send reply
                  </Button>
                </div>
              </div>
            )}

            {/* Assignment */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Assign to admin</h4>
              <div className="flex gap-3">
                <select
                  value={assignToAdmin}
                  onChange={(e) => setAssignToAdmin(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="">Select admin...</option>
                  {users.filter((u) => u.role === UserRole.SUPER_ADMIN).map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.firstName} {admin.lastName}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleAssignTicket}
                  disabled={!assignToAdmin || isAssigning}
                  className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {isAssigning ? 'Assigning...' : 'Assign'}
                </Button>
              </div>
              {ticket.assignedTo && (
                <p className="text-sm text-gray-600 mt-2">
                  Currently assigned to:{' '}
                  <span className="font-medium text-gray-900">
                    {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}
                  </span>
                </p>
              )}
            </div>

            {/* Status */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Status</h4>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TicketStatus)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              >
                <option value={TicketStatus.OPEN}>Open</option>
                <option value={TicketStatus.IN_PROGRESS}>In Progress</option>
                <option value={TicketStatus.WAITING_ON_EMPLOYEE}>Waiting on Employee</option>
                <option value={TicketStatus.RESOLVED}>Resolved</option>
                <option value={TicketStatus.CLOSED}>Closed</option>
              </select>
              {statusError && <p className="mt-2 text-xs text-red-600">{statusError}</p>}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 px-6 py-2.5 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-medium"
            >
              Close
            </Button>
            <Button
              onClick={handleUpdateStatus}
              disabled={isUpdating || status === ticket.status}
              className="flex-1 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-6 py-2.5 rounded-xl text-sm font-medium disabled:opacity-50"
            >
              {isUpdating ? 'Updating...' : 'Save status'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AdminMessageBubble({ message }: { message: SupportMessage }) {
  // Admin is the viewer here: their own messages right-align with a blue pill,
  // employees left-align with gray, employers left-align with primary orange.
  // Matches the EmployerConversationsPanel bubble pattern so the chat feels
  // consistent across roles.
  const isAdmin = message.senderType === 'admin';
  const pillStyle =
    message.senderType === 'admin'
      ? 'bg-blue-50 text-blue-700'
      : message.senderType === 'employer'
        ? 'bg-[#FF6B35]/10 text-[#FF6B35]'
        : 'bg-gray-100 text-gray-700';
  const pillLabel =
    message.senderType === 'admin'
      ? 'You'
      : message.senderType === 'employer'
        ? 'Employer'
        : 'Employee';
  const bubbleStyle = isAdmin
    ? 'bg-blue-50 border border-blue-100'
    : message.senderType === 'employer'
      ? 'bg-[#FFF8F4] border border-[#FF6B35]/20'
      : 'bg-[#fafafa] border border-[#f0f0f0]';

  return (
    <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] flex flex-col gap-1 ${isAdmin ? 'items-end' : 'items-start'}`}>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${pillStyle}`}>
            {pillLabel}
          </span>
          {message.senderName && (
            <span className="text-[11px] text-gray-500">{message.senderName}</span>
          )}
          <span className="text-[11px] text-gray-400">{formatWhen(message.createdAt)}</span>
        </div>
        <div className={`px-3 py-2 rounded-lg text-sm whitespace-pre-wrap leading-relaxed ${bubbleStyle}`}>
          {message.body}
        </div>
      </div>
    </div>
  );
}
